/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  AuthenticatePasskeyRequestPublicKey,
  AuthenticatePasskeyResponse,
  AuthenticatorRegistrationOptionResponse,
  ClientExtensionResults,
  CreatePasskeyResponse,
} from '../sso/models';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/types';
import { normalizePasskeyOptions } from '../../utils/passkeyRpId';

const isBrowserEnvironment = () => {
  try {
    return typeof window !== 'undefined' && typeof navigator !== 'undefined';
  } catch {
    return false;
  }
};

export const isPasskeySupported = (): boolean => {
  if (!isBrowserEnvironment()) return false;
  try {
    return !!window.PublicKeyCredential;
  } catch {
    return false;
  }
};

class WebPasskeyManager {
  async create(
    createPasskeyRequest: AuthenticatorRegistrationOptionResponse,
  ): Promise<CreatePasskeyResponse> {
    const { startRegistration } = await import('@simplewebauthn/browser');
    const credential = await startRegistration(
      normalizePasskeyOptions(
        createPasskeyRequest,
      ) as PublicKeyCredentialCreationOptionsJSON,
    );

    /** Matches browser JSON sent to SSO (includes `authenticatorData` when the browser exposes it). */
    return {
      id: credential.id,
      rawId: credential.rawId,
      type: credential.type,
      authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
      clientExtensionResults:
        credential.clientExtensionResults as ClientExtensionResults,
      response: { ...credential.response },
    };
  }

  async authenticate(
    authenticatePasskeyRequest: AuthenticatePasskeyRequestPublicKey,
  ): Promise<AuthenticatePasskeyResponse> {
    if (!authenticatePasskeyRequest.challenge) {
      throw new Error('Missing passkey challenge.');
    }

    const allowCredentials = authenticatePasskeyRequest.allowCredentials
      ?.filter(
        (credential) => typeof credential.id === 'string' && credential.id,
      )
      .map((credential) => ({
        ...credential,
        type: credential.type ?? 'public-key',
      }));

    const { startAuthentication } = await import('@simplewebauthn/browser');
    const credential = await startAuthentication({
      ...normalizePasskeyOptions(authenticatePasskeyRequest),
      allowCredentials,
    } as PublicKeyCredentialRequestOptionsJSON);

    return {
      id: credential.id,
      rawId: credential.rawId,
      type: credential.type,
      authenticatorAttachment: credential.authenticatorAttachment,
      response: {
        clientDataJSON: credential.response.clientDataJSON,
        authenticatorData: credential.response.authenticatorData,
        signature: credential.response.signature,
        userHandle: credential.response.userHandle,
      },
      clientExtensionResults: credential.clientExtensionResults,
    };
  }
}

export const createPasskeyManager = () => {
  return new WebPasskeyManager();
};
